/**
 * Protected Route Component
 *
 * Route guard that checks JWT validity before rendering protected pages.
 * Redirects to login page if user is not authenticated.
 * Preserves the intended destination URL for post-login redirect.
 *
 * Usage:
 *   <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
 */

import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

interface ProtectedRouteProps {
  children: ReactNode;
}


export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { state } = useAuth();
  const location = useLocation();

  // Show loading state while authentication is being checked
  if (state.isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          fontSize: '1.2rem',
          color: '#666',
        }}
      >
        Loading...
      </div>
    );
  }

  // Redirect to login if not authenticated
  // Preserve the intended destination in the state for post-login redirect
  if (!state.isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Redirect to phone verification if phone is not verified (admins exempt)
  if (state.user && state.user.is_phone_verified !== true && state.user.role !== 'administrator') {
    return <Navigate to="/verify-phone" state={{ from: location }} replace />;
  }

  // User is authenticated, render the protected component
  return <>{children}</>;
}
